// ============================================
// ConverseTech Frontend - DET Simulation Hook
// ============================================
// State machine for controlling DET (Duolingo English Test) simulation phases

import { useState, useEffect, useCallback, useRef } from 'react';
import { DET_PHASES, type DETPhase, type DETPhaseConfig } from '@conversetech/shared';

interface UseDETSimulationOptions {
  onPhaseStart: (phase: DETPhaseConfig) => void;
  onPhaseEnd: (phase: DETPhase, overTime: boolean) => void;
  onComplete: () => void;
}

interface UseDETSimulationReturn {
  currentPhase: DETPhaseConfig | null;
  countdown: number | null;
  isActive: boolean;
  isWaitingForTask: boolean;  // True while waiting for Gemini to present the task
  totalPhases: number;
  currentPhaseIndex: number;
  isOverTime: boolean;
  startSimulation: () => void;
  nextPhase: () => void;
  stopSimulation: () => void;
  startTimer: () => void;     // Call this when Gemini finishes presenting the task
}

export function useDETSimulation(options: UseDETSimulationOptions): UseDETSimulationReturn {
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(-1);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [isOverTime, setIsOverTime] = useState(false);
  const [isWaitingForTask, setIsWaitingForTask] = useState(false);

  // Use refs for callbacks to avoid re-triggering effects
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const currentPhase = currentPhaseIndex >= 0 && currentPhaseIndex < DET_PHASES.length
    ? DET_PHASES[currentPhaseIndex]
    : null;

  // Countdown timer - only runs when NOT waiting for task
  useEffect(() => {
    if (!isActive || countdown === null || isWaitingForTask) return;

    // Check if timer has expired (but don't auto-advance - let user continue)
    if (countdown <= 0) {
      setIsOverTime(true);
      // Continue counting into negative for "over time" tracking
    }

    const timer = setTimeout(() => {
      setCountdown(prev => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, isActive, isWaitingForTask]);

  const startSimulation = useCallback(() => {
    if (DET_PHASES.length === 0) return;

    setCurrentPhaseIndex(0);
    // Don't start countdown yet - wait for Gemini to present the task
    setCountdown(Math.floor(DET_PHASES[0].duration / 1000));
    setIsActive(true);
    setIsOverTime(false);
    setIsWaitingForTask(true);  // Timer paused until startTimer() is called

    optionsRef.current.onPhaseStart(DET_PHASES[0]);
  }, []);

  const handlePhaseEnd = useCallback((overTime: boolean) => {
    if (currentPhaseIndex < 0 || currentPhaseIndex >= DET_PHASES.length) return;

    const currentPhaseConfig = DET_PHASES[currentPhaseIndex];
    optionsRef.current.onPhaseEnd(currentPhaseConfig.phase, overTime);

    if (currentPhaseIndex < DET_PHASES.length - 1) {
      // Move to next phase
      const nextIndex = currentPhaseIndex + 1;
      const nextPhaseConfig = DET_PHASES[nextIndex];

      setCurrentPhaseIndex(nextIndex);
      // Don't start countdown yet - wait for Gemini to present the task
      setCountdown(Math.floor(nextPhaseConfig.duration / 1000));
      setIsOverTime(false);
      setIsWaitingForTask(true);  // Timer paused until startTimer() is called

      optionsRef.current.onPhaseStart(nextPhaseConfig);
    } else {
      // Simulation complete
      setIsActive(false);
      setCountdown(null);
      setCurrentPhaseIndex(-1);
      setIsOverTime(false);
      setIsWaitingForTask(false);

      optionsRef.current.onComplete();
    }
  }, [currentPhaseIndex]);

  // Start the timer - call this when Gemini finishes presenting the task
  const startTimer = useCallback(() => {
    setIsWaitingForTask(false);
  }, []);

  const nextPhase = useCallback(() => {
    // Manual advance - check if over time
    const overTime = countdown !== null && countdown <= 0;
    handlePhaseEnd(overTime);
  }, [countdown, handlePhaseEnd]);

  const stopSimulation = useCallback(() => {
    setIsActive(false);
    setCountdown(null);
    setCurrentPhaseIndex(-1);
    setIsOverTime(false);
    setIsWaitingForTask(false);
  }, []);

  return {
    currentPhase,
    countdown,
    isActive,
    isWaitingForTask,
    totalPhases: DET_PHASES.length,
    currentPhaseIndex: currentPhaseIndex + 1, // 1-indexed for display
    isOverTime,
    startSimulation,
    nextPhase,
    stopSimulation,
    startTimer,
  };
}
